/**
 * Wall-hung sink — the compact realisation of a `sink` fixture whose
 * cabinet is too small to hold any real sink (below WALL_HUNG_SINK_MAX).
 *
 * One object, `type: 'sink'` with `sinkType: 'wall-mount'` (canonical
 * standalone value, shared-lib/sink-types), mounted on the
 * parent wall with its rim at the requested vanity height, plus a
 * deck-mounted faucet. No cabinet, no countertop, no `parents`.
 * Rendered by web-app `proceduralRenderers/wallHungSinkRenderer.js`,
 * which shares the deck proportions below so the faucet lands on the deck.
 */
const { WALL_HUNG_SINK, WALL_HUNG_SINK_MAX } = require('./constants');

/**
 * The picker id that means "replace this vanity with a wall-hung basin".
 *
 * A wall-hung unit is PROCEDURAL — there is no `unifiedObjects` doc for it
 * (dev's catalog holds 9 undermount, 4 vessel, 4 dropin and exactly 1
 * pedestal sink, and no wall-mount at all) — so the picker offers it as a
 * synthetic tile rather than a catalog row, and the swap tool has to
 * recognise the id instead of fetching it. Shared so the client tile and the
 * server branch cannot drift on the string.
 */
const WALL_HUNG_SINK_MODEL_ID = 'procedural-wall-hung';
const { createSinkFaucet } = require('./createSinkFaucet');
const { buildSinkDimensions } = require('../sink-types');

/** True when a requested cabinet is smaller than any sink can fit. */
function isWallHungSinkSize({ width, depth }) {
  return width < WALL_HUNG_SINK_MAX.width || depth < WALL_HUNG_SINK_MAX.depth;
}

/** Front-to-back size of the faucet deck along the unit's back edge. */
function wallHungSinkDeckDepth(depth) {
  return Math.max(
    WALL_HUNG_SINK.deckMinDepth,
    depth * WALL_HUNG_SINK.deckFraction
  );
}

function buildWallHungSink({
  roomId,
  position,
  rotation,
  floorY,
  parentWallIds,
  dimensions,
  modelData,
  finishes,
  roomData,
  fixtureGroupId,
  timestamp,
  sinkId: inputSinkId,
  addedBy = 'create_bathroom_vanity',
}) {
  const { height: rimHeight } = dimensions;
  // Both axes are CAPPED, not inherited. A scan-built unit comes from a
  // cabinet too small for any basin, so the caps rarely bind there; a
  // CONVERSION comes from a full-size vanity, where they always do.
  const width = Math.min(dimensions.width, WALL_HUNG_SINK.maxWidth);
  const depth = Math.min(dimensions.depth, WALL_HUNG_SINK.maxDepth);
  const unitHeight = WALL_HUNG_SINK.height;
  const rimY = floorY + rimHeight;
  // The scan path ADOPTS the basin RoomPlan already gave an identifier;
  // minting a fresh id there would orphan every edit, parent link and
  // design-adjustment fingerprint keyed on it, and would re-mint on every
  // reprocess. Only the chat path, which is building from nothing, mints.
  const sinkId = inputSinkId || `sink_${timestamp}_${roomId}_0`;
  const now = new Date().toISOString();

  const sink = {
    id: sinkId,
    type: 'sink',
    sinkType: 'wall-mount',
    label: 'Wall-hung Sink',
    category: { sink: true },
    center: { x: position.x, y: rimY - unitHeight / 2, z: position.z },
    // A wall-mount sink is NOT in COUNTERTOP_MOUNTED_SINK_TYPES, so unlike
    // undermount/dropin/vessel it gets no `skipCollisionCheck` — it is a live
    // collider and needs a complete footprint.
    dimensions: buildSinkDimensions({
      width,
      depth,
      height: unitHeight,
      // Wall-hung: no cabinet, no countertop (`countertopId: null` below), so
      // no hole to cut.
      cutoutDepth: null,
    }),
    rotation,
    parentWallIds,
    wallMounted: true,
    fixtureGroupId,
    addedBy,
    lastModified: now,
  };

  const faucet = createSinkFaucet({
    sinkPos: position,
    sinkId,
    sinkType: 'wall-mount',
    countertopId: null,
    countertopSurfaceY: rimY,
    rotation,
    faucetModelData: modelData?.faucetModelData,
    fixtureGroupId,
    faucetFinish: finishes?.faucetFinish,
    // Minted alongside a minted sink; when the sink id was ADOPTED, fall
    // through to createSinkFaucet's `faucet_<ts>_<sinkId>` default, which
    // is the id shape the scan path's other faucets already carry.
    faucetId: inputSinkId ? undefined : `faucet_${timestamp}_${roomId}_0`,
    label: 'Faucet',
    addedBy,
    mountType: 'counter',
    deckOffsetBehindSink: depth / 2 - wallHungSinkDeckDepth(depth) / 2,
    vanityDepth: depth,
    parentWallIds,
    walls: roomData?.walls,
  });

  return {
    objects: [sink, faucet],
    componentsCreated: ['sink', 'faucet'],
    dimensions: { width, height: rimHeight, depth },
    fixtureGroupId,
    sinkPositions: [{ x: position.x, z: position.z }],
    sinkLateralPositions: [{ x: position.x, z: position.z }],
    sinkSpacing: 0,
    countertopCenterY: rimY,
    countertopThickness: 0,
    cabinetId: null,
    countertopId: null,
  };
}

module.exports = {
  WALL_HUNG_SINK_MODEL_ID,
  isWallHungSinkSize,
  wallHungSinkDeckDepth,
  buildWallHungSink,
};
